import Vue from "vue";
import store from "./store";
import VueApollo from "vue-apollo";
// Install the vue plugin
Vue.use(VueApollo);
import possibleTypes from "./possibleTypes.json";
import { ApolloClient, createHttpLink, InMemoryCache } from "@apollo/client/core";
import { setContext } from "@apollo/client/link/context";
import { buildAuthHeaders } from "@/lib/authHeaders";
import { onError } from "@apollo/client/link/error";
import { reportForcedLogout } from "@/utils/forcedLogout";
import router from "./router";
import { isEmbedPath } from "@/utils/embedPath";

// Name of the localStorage item
const AUTH_TOKEN = "apollo-token";

// Http endpoint
const httpEndpoint =
  process.env.VUE_APP_ROOT_API + process.env.VUE_APP_GRAPHQL_HTTP ||
  "http://localhost:3000/develop/graphql";
// Files URL root
// export const filesRoot = process.env.VUE_APP_FILES_ROOT || httpEndpoint.substr(0, httpEndpoint.indexOf('/graphql'))
// Vue.prototype.$filesRoot = filesRoot

const httpLink = createHttpLink({
  uri: httpEndpoint,
});

const errorLink = onError((error) => {
  if (
    error.graphQLErrors?.length > 0 &&
    error.graphQLErrors[0] === "Error de Autenticación"
  ) {
    const currentRoute = router.currentRoute?.name;
    const skipRoutes = ["Login", "Signout", "Recover", "Change", "Register", "SSOLogin", "SSOCallback", "SSODirect"];
    // DL-452: the embed routes are public and have no legacy session. An auth error there is not
    // a forced logout, it is the expected outcome: bouncing the iframe to /acceder would be the
    // bug. Both keys are consulted because the errorLink can fire before the router resolves its
    // first navigation, while `meta` is still empty.
    const isEmbedRoute = isEmbedPath() || Boolean(router.currentRoute?.meta?.embed);
    // `Conference` NO se excluye por ruta: un usuario autenticado ahí debe seguir
    // cerrando sesión ante un error de autenticación. Solo se exceptúa el INVITADO
    // real (sin usuario en el store y con `guestToken` en la URL), que no tiene
    // sesión que cerrar y para quien el logout sería un loop de redirección.
    const isConferenceGuest =
      currentRoute === "Conference" &&
      !store.getters["users/user"] &&
      Boolean(router.currentRoute?.query?.guestToken);
    if (!isEmbedRoute && !skipRoutes.includes(currentRoute) && !isConferenceGuest) {
      reportForcedLogout({
        trigger: "apollo_error_link",
        page: currentRoute,
        userId: store.getters["users/user"]?._id,
        tags: {
          "session.operation": error.operation?.operationName || "unknown",
        },
        extra: {
          "session.graphql_errors": error.graphQLErrors,
          "session.network_error": error.networkError?.message || null,
        },
      });
      store.dispatch("users/signout");
      router.push({ name: "Login" }).catch(() => {});
    }
  }
});

const authLink = setContext((_, { headers }) => ({
  // El Authorization que traiga la operación gana sobre el token del store: es
  // como viaja el guest token del líder, que no tiene sesión iniciada.
  headers: buildAuthHeaders({
    headers,
    token: store.getters["users/getToken"],
  }),
}));

export function createClient(options = {}) {
  const apolloClient = new ApolloClient({
    ...options,
    ssrMode: false,
    link: errorLink.concat(authLink.concat(httpLink)),
    // Sin `defaultContext.headers.Authorization`: se evaluaba UNA vez, al
    // construir el cliente, y varios componentes llaman a `createClient()` a
    // nivel de módulo. Antes daba igual porque el authLink lo pisaba siempre;
    // ahora que el header de la operación manda, ese valor congelado ganaría
    // sobre el token vigente y las peticiones posteriores a un login o a un
    // signout viajarían con el token equivocado. El authLink pone el token
    // actual del store en cada operación.
    cache: new InMemoryCache({ possibleTypes, addTypename: false }),
  });
  return apolloClient;
}

// Call this in the Vue app file
export function createProvider(options = {}) {
  const apolloClient = new ApolloClient({
    ...options,
    link: errorLink.concat(authLink.concat(httpLink)),
    // Sin `defaultContext.headers.Authorization`: se evaluaba UNA vez, al
    // construir el cliente, y varios componentes llaman a `createClient()` a
    // nivel de módulo. Antes daba igual porque el authLink lo pisaba siempre;
    // ahora que el header de la operación manda, ese valor congelado ganaría
    // sobre el token vigente y las peticiones posteriores a un login o a un
    // signout viajarían con el token equivocado. El authLink pone el token
    // actual del store en cada operación.
    cache: new InMemoryCache({ possibleTypes, addTypename: false }),
  });

  // Create vue apollo provider
  const apolloProvider = new VueApollo({
    defaultClient: apolloClient,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "network-only",
      },
      query: {
        loadingKey: "loading",
      },
    },
    errorHandler(error) {
      // eslint-disable-next-line no-console
      console.error(
        "%cError",
        "background: red; color: white; padding: 2px 4px; border-radius: 3px; font-weight: bold;",
        error.message
      );
    },
  });
  return apolloProvider;
}

// Manually call this when user log in
export async function onLogin(apolloClient, token) {
  if (typeof localStorage !== "undefined" && token) {
    localStorage.setItem(AUTH_TOKEN, token);
  }
  try {
    await apolloClient.resetStore();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("%cError on cache reset (login)", "color: orange;", e.message);
  }
}

// Manually call this when user log out
export async function onLogout(apolloClient) {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(AUTH_TOKEN);
  }
  try {
    await apolloClient.resetStore();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("%cError on cache reset (logout)", "color: orange;", e.message);
  }
}
